import Algorithm from "@components/global/new/Algorithm/Algorithm";
import Icon from "@components/global/new/Icon/Icon";
import Status from "@components/global/new/Status/Status";
import { ALGOS_E } from "@constants/Algos";
import { getDevivedHash } from "@utils/hash.utils";
import { memo, useState } from "react";
import { MinerRowProps } from "../MinerRow/MinerRow.props";
import ColoredChanges from "./ColoredChanges/ColoredChanges";
import MinerIdField from "./MinerIdField/MinerIdField";
import styles from "./MinerPhoneSizeRow.module.scss";
import mstyles from "../Machines.module.scss";
import { useMinerRowControl } from "./useMinerRowControl";
import connect_icon from "@assets/icons/connect.svg";
import delete_icon from "@assets/redesign/icons/trash.svg";
import { MINER_STATUSES_E } from "@constants/MinerStatuses";
import cn from "classnames";
import Checkbox from "@components/global/new/Checkbox/Checkbox";
import { MinerSortFields } from "../MinersTable/useMinersTableControl";
import MinerRowMore from "./MinerRowMore/MinerRowMore";
import { TableHeadItem } from "../MinersTable/tableHead";
import useMinerChangeControl from "@utils/hooks/useMinerChangeControl";
import { Element } from "react-scroll";
import RejectedChart from "@components/global/RejectedChart";
import { useIntl } from "react-intl";
import { useMinerContext } from "./MinerContext";

export interface MinerPhoneSizeRowProps extends MinerRowProps {
  pos: number;
  header: TableHeadItem[];
}

interface RowRowPropsI {
  headItem: TableHeadItem;
  children: JSX.Element;
  isFirst?: boolean;
  isLast?: boolean;
  isOdd?: boolean;
  checked?: boolean;
  onClick?: () => void;
}

function RowItem(props: RowRowPropsI) {
  const { formatMessage } = useIntl();

  return (
    <div className={styles.rowItem}>
      <div className={styles.head}>
        {formatMessage({ id: props.headItem.messageId })}
      </div>
      <div
        className={cn(styles.data, {
          [styles.first]: props.isFirst,
          [styles.last]: props.isLast,
          [styles.odd]: props.isOdd,
        })}
      >
        {props.headItem.sort === MinerSortFields.ID && (
          <div style={{ marginRight: 10 }}>
            <Checkbox checked={props.checked} onClick={props.onClick} />
          </div>
        )}
        {props.children}
      </div>
    </div>
  );
}

function MinerPhoneSizeRow(props: MinerPhoneSizeRowProps) {
  const { miner } = useMinerContext();
  const { formatMessage } = useIntl();
  const control = useMinerRowControl();
  const changeCtrl = useMinerChangeControl(miner.id);

  return (
    <Element
      className={cn(styles.container, {
        [styles.change]: changeCtrl.changed,
      })}
      name={miner.id.toString()}
    >
      <div
        className={cn(styles.row, {
          [styles.odd]: props.pos % 2,
        })}
      >
        <RowItem
          headItem={props.header[0]}
          isFirst
          isOdd
          checked={control.isChecked}
          onClick={() => props.selectMiner(miner)}
        >
          <div className={mstyles.table_item}>
            <MinerIdField text={miner.miner_id} minerId={miner.id} />
          </div>
        </RowItem>
        <RowItem headItem={props.header[1]}>
          <ColoredChanges className={mstyles.table_item}>
            {miner.profile?.name}
          </ColoredChanges>
        </RowItem>
        <RowItem headItem={props.header[2]} isOdd>
          <div className={mstyles.table_item}>
            <Algorithm algo={miner.hash as ALGOS_E} string />
          </div>
        </RowItem>
        <RowItem headItem={props.header[3]}>
          <ColoredChanges className={mstyles.table_item}>
            {getDevivedHash(miner.speed_default)}
          </ColoredChanges>
        </RowItem>
        <RowItem headItem={props.header[4]} isOdd>
          <ColoredChanges className={mstyles.table_item}>
            {getDevivedHash(miner.speed_current)}
          </ColoredChanges>
        </RowItem>
        <RowItem headItem={props.header[5]}>
          <ColoredChanges className={mstyles.table_item}>
            {miner.setting}
          </ColoredChanges>
        </RowItem>
        <RowItem headItem={props.header[6]} isOdd>
          <div
            className={mstyles.table_item}
            style={{ display: "flex", justifyContent: "center", gap: 10 }}
          >
            <Status
              status={miner.status}
              admin_mining={miner.admin_mining_do}
            />
            <RejectedChart
              rejected={miner.rejected}
              accepted={miner.accepted}
            />
          </div>
        </RowItem>
        <RowItem headItem={props.header[7]}>
          <ColoredChanges className={styles.table_item}>
            {miner.location_raw?.name ||
              formatMessage({ id: "machines.table.locations.empty" })}
          </ColoredChanges>
        </RowItem>
        {control.isAdmin && (
          <RowItem headItem={props.header[8]} isLast isOdd>
            <div
              className={mstyles.table_item}
              style={{ justifySelf: "flex-end" }}
            >
              <div className={styles.newMiner_btns}>
                <Icon src={connect_icon} onClick={control.onConnectHandler} />
                {miner.status === MINER_STATUSES_E.OFF && (
                  <Icon src={delete_icon} onClick={control.onDeleteHandler} />
                )}
              </div>
              <ColoredChanges
                style={{
                  marginTop: 5,
                  justifySelf: "flex-end",
                  wordBreak: "break-all",
                  maxWidth: "100%",
                  width: "100%",
                }}
              >
                {miner.customer_email}
              </ColoredChanges>
            </div>
          </RowItem>
        )}
      </div>
      <MinerRowMore />
    </Element>
  );
}

export default memo(MinerPhoneSizeRow);
